import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Button, showSnackbar, TextInput } from 'fluxo-ui';
import { AwardIcon, CheckCircleIcon, TimesIcon } from 'fluxo-ui/icons';

import certificateService, { CertificateVerification } from '../services/certificate.service';

/** Public certificate verification page (no login required). */
const VerifyCertificatePage: React.FC = () => {
    const { code } = useParams<{ code: string }>();
    const [inputCode, setInputCode] = useState(code || '');
    const [result, setResult] = useState<CertificateVerification | null>(null);
    const [checking, setChecking] = useState(false);

    const verify = async (verificationCode: string) => {
        if (!verificationCode.trim()) return;
        setChecking(true);
        setResult(null);
        try {
            const verification = await certificateService.verify(verificationCode.trim());
            setResult(verification);
        } catch (error: any) {
            showSnackbar(error.message || 'Verification failed', 'Error', { type: 'error' });
        } finally {
            setChecking(false);
        }
    };

    useEffect(() => {
        if (code) {
            verify(code);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    return (
        <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-lg space-y-6">
                <div className="text-center">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-r from-teal-600 to-cyan-600 text-white flex items-center justify-center shadow-lg">
                        <AwardIcon className="w-7 h-7" />
                    </div>
                    <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Certificate Verification</h1>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Enter a verification code to confirm a Testam certificate is authentic.
                    </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 space-y-4">
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <TextInput
                                value={inputCode}
                                onChange={(e) => setInputCode(e.value)}
                                placeholder="e.g. W19U8DC9P5PQFR9H"
                                onKeyPress={(e: any) => {
                                    if (e.key === 'Enter') verify(inputCode);
                                }}
                            />
                        </div>
                        <Button label="Verify" variant="primary" onClick={() => verify(inputCode)} isLoading={checking} />
                    </div>

                    {result && (
                        <div
                            className={`rounded-xl border p-5 ${
                                result.valid
                                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            }`}
                        >
                            <div className="flex items-center gap-2 font-semibold">
                                {result.valid ? (
                                    <>
                                        <CheckCircleIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                        <span className="text-emerald-800 dark:text-emerald-200">Certificate is valid</span>
                                    </>
                                ) : (
                                    <>
                                        <TimesIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                                        <span className="text-red-800 dark:text-red-200">{result.message || 'Certificate not found'}</span>
                                    </>
                                )}
                            </div>
                            {result.valid && result.certificate && (
                                <dl className="mt-4 space-y-2 text-sm">
                                    <div className="flex justify-between gap-4">
                                        <dt className="text-slate-500 dark:text-slate-400">Awarded to</dt>
                                        <dd className="font-medium text-slate-900 dark:text-white">{result.certificate.userName}</dd>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <dt className="text-slate-500 dark:text-slate-400">Exam</dt>
                                        <dd className="font-medium text-slate-900 dark:text-white text-right">
                                            {result.certificate.examTitle}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <dt className="text-slate-500 dark:text-slate-400">Score</dt>
                                        <dd className="font-medium text-slate-900 dark:text-white">
                                            {Math.round(result.certificate.score * 10) / 10}%
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <dt className="text-slate-500 dark:text-slate-400">Issued</dt>
                                        <dd className="font-medium text-slate-900 dark:text-white">
                                            {new Date(result.certificate.issuedAt).toLocaleDateString()}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                        <dt className="text-slate-500 dark:text-slate-400">Certificate ID</dt>
                                        <dd className="font-mono text-xs text-slate-900 dark:text-white">
                                            {result.certificate.certificateId}
                                        </dd>
                                    </div>
                                </dl>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyCertificatePage;
